import { Dayjs } from 'dayjs';
import { ReserveApartmentData } from './apartments';
import { Application, CensoredApplication } from './applications';
export type ReservableType = 'bestReservable' | 'reservable';
export const ShowingTypes = {
    openHouse: 'openHouse' as const,
    personal: 'personal' as const
};
export type ShowingType = typeof ShowingTypes.personal | typeof ShowingTypes.openHouse;
/** showing is always 30 min */
export type ShowingDuration = 0;
export interface CreateShowing {
    /** showing duration in minutes. always 30 min */
    duration: ShowingDuration;
    /** start date and time (ISO 8601) */
    startDate: string;
    extraStartDates?: string[];
    /** end date and time (ISO 8601) */
    endDate: string;
    type: ShowingType;
}
/** todo, remove form showing */
export interface FormShowing extends CreateShowing {
    type: ShowingType;
}
export interface DeleteShowingsFormData {
    startDate: Dayjs;
    showings?: FormShowing[];
}
export interface AddShowingsFormData {
    /** yyyy/mm/dd */
    date: Dayjs;
    startDate: Dayjs;
    showings?: FormShowing[];
    directCreation?: boolean;
}
/** only used to allow for Reservable and Reserved typing and isReserved typeGuard */
export interface BaseShowingProps extends CreateShowing {
    /** Primary partition key */
    apartmentId: string;
    /** Sort key, use new format at some point ? `${yearmmddhhmm}-${showingId}` */
    showingId: string;
    /** userId, same as apartments */
    userId: string;
    /** date when created (unix epoch) */
    createdAt: number;
    /** date when updated (unix epoch) */
    updatedAt?: number;
    type: ShowingType;
}
/** showing that can be reserved. (applicationIds = applications who reserved)
 *
 * openHouse can have applicationIds
 *
 * personal reservable can not have applicationIds
 *  */
export interface ReservableShowing extends BaseShowingProps {
    /** ids of applications that have reserved the showing */
    applicationIds?: string[];
    invitedApplicationIds?: string[];
}
/** Reserved always has applicationIds defined (applicationIds = applications who reserved) */
export interface ReservedShowing extends BaseShowingProps {
    applicationIds: string[];
    invitedApplicationIds?: string[];
}
/** represents the showings an applicant can be suggested/reserve when they enter reserve view */
export interface ReservableShowings {
    /** any reservable showing */
    reservables: ReservableShowing[];
    /** reservables we want to suggest first */
    bestReservables: ReservableShowing[];
}
export type Showing = ReservableShowing | ReservedShowing;
/** type guard so we can use showing.applicationIds, also allows us to easily check if showing is reserved */
export const checkIfReservedShowing = (showing: Showing): showing is ReservedShowing => (showing as ReservedShowing).applicationIds !== undefined;
export const isDeletableShowing = (showing: FormShowing | Showing): showing is Showing => !(showing as Showing).applicationIds &&
    !!(showing as Showing).userId &&
    !!(showing as Showing).apartmentId &&
    !!(showing as Showing).showingId;
export interface ReservationRequest {
    showingId: string;
}
/**
 * All data required in showing reservation process by apartment applicant
 */
export interface ReserveData {
    application: CensoredApplication;
    /**
     * apartments future showings.
     * exists only when applicant do not have any showings reserved already
     */
    reservableShowings: ReservableShowings;
    /**
     * true: it is possible to arrange showings
     * false: it is not possible to arrange showings anymore
     * */
    isInMarketing: boolean;
    apartment: ReserveApartmentData;
    reserved?: ReservedShowing;
    invited?: Showing[];
}
/** applications currently added in frontEnd */
export interface Reservation extends ReservedShowing {
    applications: Application[];
}
export interface CalendarEvent {
    title: string;
    start: Date;
    end: Date;
    id?: string;
    showingId: string;
    type: string;
    status: string;
    duration: number;
    resource?: any;
}
export interface SimpleCalendarEvent {
    date: Date;
    view: string;
}
export interface ReservationsProp {
    reservations: ReservedShowing[];
}
